import React from 'react';
import { Link } from 'react-router-dom';
import { FEATURES, CLIENTS } from '../../data/landing';

// FEATURES + CLIENTS imported from shared data/landing.js

const values = [
  { icon: '&#x1F4A7;', iconClass: 'ic-blue', title: 'Purity First', desc: 'Every drop passes through a 7-stage RO + UV purification system. We never compromise on water quality.' },
  { icon: '&#x1F69A;', iconClass: 'ic-green', title: 'Reliable Delivery', desc: 'Rain or shine, 365 days a year. Our delivery team ensures your order reaches you on time, every time.' },
  { icon: '&#x2705;', iconClass: 'ic-teal', title: 'Certified Safe', desc: 'ISI, FSSAI and ISO certified. Our water undergoes regular third-party lab tests so you drink with confidence.' },
];

const certifications = [
  { code: 'ISI', name: 'Bureau of Indian Standards', body: 'BIS Certified' },
  { code: 'FSSAI', name: 'Food Safety & Standards Authority', body: 'License No: 12345678' },
  { code: 'ISO', name: 'ISO 9001:2015 Quality', body: 'Quality Assured' },
  { code: 'WHO', name: 'WHO GMP Compliant', body: 'Good Manufacturing Practice' },
];


const PACKAGING_POINTS = [
  {
    title: 'Expert Production Oversight',
    desc: 'Our production facility is managed by experienced technical and operational personnel who supervise every stage of purification, filling, sealing and packaging through advanced automated systems.',
  },
  {
    title: 'In-House PET Bottle Manufacturing',
    desc: 'The PET bottles used for packing 250 ML, 500 ML and 1 Litre water are produced within our own facility under controlled hygienic conditions. This in-house bottle-manufacturing process minimises external handling and reduces the possibility of contamination. Before filling, every bottle is thoroughly cleaned using purified water processed within the plant.',
  },
  {
    title: 'Automated 20 L Jar Processing',
    desc: 'The 20 Litre jars are processed through a completely automated washing, sterilisation, filling and capping line. Each reusable jar undergoes a detailed multi-step cleaning procedure before refilling and dispatch.',
  },
  {
    title: 'Multi-Step Cleaning Cycle',
    desc: 'The cleaning cycle includes outer surface washing and scrubbing, internal rinsing, food-grade sanitisation, hot water treatment and a final rinse with purified product water to maintain the highest hygiene standards.',
  },
  {
    title: 'Tamper-Proof Heat Shrink Sealing',
    desc: 'After filling, the jars are automatically capped and secured with tamper-proof heat shrink seals to preserve purity and ensure safe delivery to customers without any risk of adulteration.',
  },
];

const EQUIPMENT = [
  {
    tag: 'Labelling',
    title: 'Fully Automatic BOPP Labelling System',
    desc: 'Ensures accurate, high-speed and hygienic labelling of bottles with superior finishing and consistency. The automated process minimises manual handling and helps maintain product quality and packaging standards.',
  },
  {
    tag: 'Wrapping',
    title: 'Fully Automatic Shrink Wrapping System',
    desc: 'Provides secure and efficient packaging of products, ensuring better protection during handling, storage and transportation while maintaining neat and professional presentation standards.',
  },
];

const process = [
  { num: '01', title: 'Source Water Management', desc: 'Deep groundwater is drawn from certified borewells; raw-water quality is monitored continuously to ensure a reliable, pristine source.' },
  { num: '02', title: 'Multi-Layer Filtration System', desc: 'Sediment, activated-carbon and softening filters remove suspended particles, odour, chlorine and hardness.' },
  { num: '03', title: 'Advanced RO Purification', desc: 'Reverse-osmosis membranes reduce TDS and eliminate dissolved salts, heavy metals and microbial contaminants.' },
  { num: '04', title: 'UV Disinfection', desc: 'High-intensity ultraviolet exposure inactivates any residual bacteria, viruses and pathogens for sterile output.' },
  { num: '05', title: 'Essential Mineral Balancing', desc: 'Beneficial minerals are reintroduced at optimal levels for balanced TDS, taste and health value.' },
  { num: '06', title: 'Fine Micron Filtration', desc: 'Sub-micron polishing filters trap the smallest particulates for crystal-clear water.' },
  { num: '07', title: 'Ozone Protection', desc: 'Double ozonisation in storage and at bottling preserves freshness and prevents recontamination.' },
  { num: '08', title: 'Hygienic Packaging System', desc: 'Bottles and jars are filled and capped in a dust-free, contactless line under cleanroom-grade conditions.' },
  { num: '09', title: 'Batch Monitoring & Quality Checks', desc: 'Every batch undergoes 114 quality tests before dispatch to ensure full FSSAI compliance.' },
];

export default function AboutPage() {
  return (
    <div style={{ background: '#ffffff' }}>

      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-container">
          <div style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: '9999px', padding: '0.3rem 1rem', fontSize: '0.8rem', fontWeight: 700, marginBottom: '1rem' }}>
            &#x2B50; About Us
          </div>
          <h1>About Vijaya Varshi<br/>Water Plant</h1>
          <p>Hyderabad's trusted source for pure, safe drinking water &#8212; delivering quality since 2026.</p>
        </div>
      </section>

      {/* Story + Stats */}
      <section className="section" style={{ background: 'white' }}>
        <div className="page-container">
          <div className="grid grid-2" style={{ gap: '3.5rem', alignItems: 'center' }}>
            <div>
              <span className="section-badge">&#x1F4D6; Our Story</span>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '1.25rem' }}>
                From a Small Idea to<br/>Hyderabad's Trusted Brand
              </h2>
              <p className="text-justify" style={{ color: 'var(--text-body)', lineHeight: 1.85, marginBottom: '1rem' }}>
                Founded in 2026 in Hyderabad, Vijaya Varshi Water Plant started with a simple mission:
                make clean, affordable drinking water accessible to every household and business in the city.
              </p>
              <p className="text-justify" style={{ color: 'var(--text-body)', lineHeight: 1.85, marginBottom: '1rem' }}>
                What began as a small operation has grown into one of Hyderabad's most trusted water brands.
                Today we serve over 1,000 families and businesses with our iconic 20L jar and range of Vijaya Varshi branded bottles.
              </p>
              <p className="text-justify" style={{ color: 'var(--text-body)', lineHeight: 1.85 }}>
                Our water is sourced, purified and packaged under strict quality controls so you always get the purest water possible.
              </p>
            </div>
            <div className="grid grid-2" style={{ gap: '1rem' }}>
              {[
                { value: '2026', label: 'Year Founded', icon: '&#x1F3E2;' },
                { value: '1000+', label: 'Happy Customers', icon: '&#x1F600;' },
                { value: '1st', label: 'Year of Service', icon: '&#x1F4C5;' },
                { value: '365', label: 'Delivery Days/Year', icon: '&#x1F69A;' },
              ].map(s => (
                <div key={s.label} className="pub-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: s.icon }} />
                  <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--brand)', marginBottom: '0.2rem' }}>{s.value}</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Water Treatment & Quality Assurance Process */}
      <section className="section" style={{ background: 'var(--brand-xlight)' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x1F52C; Water Treatment &amp; Quality Assurance</span>
            <h2 className="section-title">Our Water Treatment &amp; Quality Assurance Process</h2>
            <p className="section-subtitle">
              At Vijaya Varshi, purity is achieved through a carefully designed multi-stage purification system that ensures safe, hygienic and great-tasting drinking water for every consumer.
            </p>
          </div>
          <div className="grid grid-3" style={{ gap: '1.25rem' }}>
            {process.map(p => (
              <div key={p.num} className="pub-card" style={{ padding: '1.75rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ minWidth: 40, height: 40, borderRadius: '50%', background: 'var(--brand)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8125rem', flexShrink: 0 }}>
                  {p.num}
                </div>
                <div>
                  <h4 style={{ fontWeight: 700, color: 'var(--text-dark)', marginBottom: '0.35rem', fontSize: '0.9375rem' }}>{p.title}</h4>
                  <p className="text-justify" style={{ color: 'var(--text-muted)', fontSize: '0.8375rem', lineHeight: 1.65 }}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging & Hygiene Standards */}
      <section className="section" style={{ background: 'white' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x1F4E6; Packaging &amp; Hygiene</span>
            <h2 className="section-title">Packaging &amp; Hygiene Standards</h2>
            <p className="section-subtitle">
              At Vijaya Varshi, special attention is given to packaging and handling practices to ensure that the water remains fresh, safe and hygienically protected until it reaches the consumer.
            </p>
          </div>

          <div className="pkg-timeline">
            {PACKAGING_POINTS.map((p, i) => (
              <div key={i} className="pkg-row">
                <div className="pkg-node">
                  <span className="pkg-num">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="pkg-card">
                  <h4 className="pkg-title">{p.title}</h4>
                  <p className="text-justify pkg-text">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="pkg-banner">
            <span className="pkg-banner-check" aria-hidden="true">&#10003;</span>
            <p>
              Through advanced technology, strict hygiene practices and continuous quality monitoring,
              <strong> Vijaya Varshi </strong>
              ensures safe and refreshing packaged drinking water for every consumer.
            </p>
          </div>

          <div className="pkg-equip-header">
            <span className="section-badge" style={{ background: 'white' }}>&#x2699; Production Equipment</span>
            <h3 className="pkg-equip-heading">Backed by Fully-Automated Systems</h3>
          </div>
          <div className="grid grid-2 pkg-equip-grid">
            {EQUIPMENT.map(e => (
              <div key={e.title} className="pkg-equip-card">
                <div className="pkg-equip-top">
                  <span className="pkg-equip-tag">{e.tag}</span>
                  <span className="pkg-equip-auto">FULLY AUTOMATIC</span>
                </div>
                <h3 className="pkg-equip-title">{e.title}</h3>
                <p className="text-justify pkg-equip-desc">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section" style={{ background: 'var(--brand-xlight)' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x1F4AA; Our Values</span>
            <h2 className="section-title">Mission &amp; Values</h2>
            <p className="section-subtitle">Everything we do is guided by our commitment to purity, reliability and customer trust.</p>
          </div>
          <div className="grid grid-3" style={{ gap: '1.5rem' }}>
            {values.map(v => (
              <div key={v.title} className="pub-card" style={{ padding: '2rem', textAlign: 'center' }}>
                <div className={'icon-circle ' + v.iconClass} dangerouslySetInnerHTML={{ __html: v.icon }} />
                <h3 style={{ fontWeight: 700, color: 'var(--text-dark)', marginBottom: '0.5rem', fontSize: '1.0625rem' }}>{v.title}</h3>
                <p className="text-justify" style={{ color: 'var(--text-muted)', lineHeight: 1.75, fontSize: '0.9rem' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Vijaya Varshi */}
      <section className="section" style={{ background: 'white' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x2728; Why Choose Us</span>
            <h2 className="section-title">Why Choose Vijaya Varshi?</h2>
            <p className="section-subtitle">Quality you can taste &mdash; verified at every step, from source to your home.</p>
          </div>
          <div className="grid grid-3" style={{ gap: '1.25rem' }}>
            {FEATURES.map(f => (

              <div key={f.title} className="pub-card feature-card">
                <span className="feature-check" aria-hidden="true">&#10003;</span>
                <div>
                  <h3 className="feature-title">{f.title}</h3>
                  <p className="text-justify feature-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section" style={{ background: 'var(--brand-xlight)' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span className="section-badge">&#x1F3C5; Certified Quality</span>
            <h2 className="section-title">Quality Certifications</h2>
            <p className="section-subtitle">Vijaya Varshi water meets national and international safety standards.</p>
          </div>
          <div className="grid grid-4" style={{ gap: '1.25rem' }}>
            {certifications.map(c => (
              <div key={c.code} className="pub-card" style={{ padding: '1.75rem', textAlign: 'center' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontWeight: 800, color: 'white', fontSize: '0.8rem', letterSpacing: '0.03em' }}>
                  {c.code}
                </div>
                <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-dark)', marginBottom: '0.3rem' }}>{c.name}</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Our Clients */}
      <section className="clients-section" style={{ background: 'white', padding: '3.5rem 1.5rem' }}>
        <div className="page-container">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <span className="section-badge">Trusted Partnerships</span>
            <h2 className="section-title" style={{ marginBottom: '0.4rem' }}>Our Clients</h2>
            <p className="section-subtitle" style={{ marginBottom: 0 }}>
              Proudly serving leading enterprises, banks and government institutions across India.
            </p>
          </div>
          <div className="clients-marquee">
            <div className="clients-track">
              {[...CLIENTS, ...CLIENTS].map((c, i) => (
                <div key={i} className="client-logo" title={c.name}>
                  {c.logo && (
                    <img
                      src={c.logo}
                      alt={c.name}
                      onError={e => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                  )}
                  <span className="client-logo-text" style={{ display: c.logo ? 'none' : 'block' }}>
                    {c.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--brand)', padding: '3.5rem 1.5rem', textAlign: 'center' }}>
        <div className="page-container">
          <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
            Ready to experience the difference?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '2rem', fontSize: '1rem' }}>
            Join thousands of satisfied customers across Hyderabad.
          </p>
          <Link to="/contact" style={{ background: 'white', color: 'var(--brand)', padding: '0.85rem 2.25rem', borderRadius: '10px', fontWeight: 700, fontSize: '1rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            Get in Touch &#8594;
          </Link>
        </div>
      </section>
    </div>
  );
}
